import React, {useState} from 'react';
import {Link, NavLink} from 'react-router-dom';
import {SunIcon, MoonIcon, Bars3Icon, XMarkIcon} from '@heroicons/react/24/outline';
import useTheme from '../hooks/useTheme';
import ThemedLogo from './ThemedLogo';
import PlaygroundBanner from './PlaygroundBanner';

const navLinkClass = ({isActive}) =>
  `text-sm px-3 py-1.5 transition ${
    isActive
      ? 'text-slate-900 dark:text-white font-medium'
      : 'text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white'
  }`;

const githubIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 sm:h-5 sm:w-5 fill-current">
    <path d="M12 .5C5.73.5.75 5.48.75 11.75c0 4.97 3.22 9.18 7.69 10.67.56.1.77-.24.77-.54 0-.27-.01-1.16-.02-2.1-3.13.68-3.79-1.33-3.79-1.33-.51-1.31-1.25-1.66-1.25-1.66-1.02-.7.08-.69.08-.69 1.13.08 1.72 1.16 1.72 1.16 1 1.72 2.63 1.22 3.27.93.1-.73.39-1.22.71-1.5-2.5-.28-5.13-1.25-5.13-5.57 0-1.23.44-2.24 1.16-3.03-.12-.29-.5-1.44.11-3 0 0 .95-.3 3.1 1.16.9-.25 1.86-.37 2.82-.38.96.01 1.92.13 2.82.38 2.15-1.46 3.1-1.16 3.1-1.16.61 1.56.23 2.71.11 3 .72.79 1.16 1.8 1.16 3.03 0 4.33-2.63 5.28-5.14 5.56.4.34.76 1.02.76 2.05 0 1.48-.01 2.67-.01 3.03 0 .3.2.65.78.54 4.46-1.49 7.68-5.7 7.68-10.66C23.25 5.48 18.27.5 12 .5z"/>
  </svg>
);

const signInClass =
  'inline-flex items-center justify-center font-medium transition rounded-md px-3 py-1.5 text-sm h-8 border border-slate-900/15 dark:border-gray-100/15 text-slate-900 dark:text-gray-100 hover:bg-slate-50 dark:hover:bg-gray-800 whitespace-nowrap shrink-0';

export default function PublicNav({showAuth = true}) {
  const {theme, toggle: toggleTheme} = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  const themeButton = (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label="Toggle theme"
      title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      className="p-1 sm:p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-200 transition"
    >
      {theme === 'dark'
        ? <SunIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true"/>
        : <MoonIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true"/>}
    </button>
  );

  return (
    <>
      <PlaygroundBanner />

      <header className="sticky top-0 z-30 backdrop-blur-md bg-surface dark:bg-gray-870">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <NavLink to="/" end aria-label="Home" className="flex items-center shrink-0">
            <ThemedLogo alt="Predico" className="block h-11 sm:h-12 w-auto shrink-0 translate-y-[2px]"/>
          </NavLink>

          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-1 sm:gap-2 shrink-0">
            <NavLink to="/about" className={navLinkClass}>About</NavLink>
            <NavLink to="/markets" className={navLinkClass}>Markets</NavLink>
            <a href="/docs/" target="_blank" rel="noopener noreferrer"
               className="text-sm text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white px-3 py-1.5">
              Docs
            </a>

            <a
              href="https://github.com/INESCTEC/predico-collabforecast"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub repository"
              title="GitHub repository"
              className="p-1 sm:p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-200 transition"
            >
              {githubIcon}
            </a>
            {themeButton}

            {showAuth && (
              <>
                <span className="mx-1 sm:mx-2 h-5 w-px bg-slate-900/10 dark:bg-gray-100/10" aria-hidden="true"/>
                <Link to="/signin" className={signInClass}>Sign in</Link>
              </>
            )}
          </div>

          {/* Mobile controls */}
          <div className="flex sm:hidden items-center gap-1 shrink-0">
            {themeButton}
            {showAuth && (
              <Link to="/signin" className={signInClass}>Sign in</Link>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-200 transition"
            >
              {menuOpen
                ? <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
                : <Bars3Icon className="h-5 w-5" aria-hidden="true"/>}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="sm:hidden border-t border-slate-900/10 dark:border-gray-100/10 px-6 py-2 flex flex-col items-start">
            <NavLink to="/about" className={navLinkClass} onClick={() => setMenuOpen(false)}>About</NavLink>
            <NavLink to="/markets" className={navLinkClass} onClick={() => setMenuOpen(false)}>Markets</NavLink>
            <a href="/docs/" target="_blank" rel="noopener noreferrer"
               onClick={() => setMenuOpen(false)}
               className="text-sm text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white px-3 py-1.5">
              Docs
            </a>
            <a
              href="https://github.com/INESCTEC/predico-collabforecast"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white px-3 py-1.5"
            >
              {githubIcon}
              <span>GitHub</span>
            </a>
          </div>
        )}
      </header>
    </>
  );
}
